import { apiRequest } from '@/utils/api-request';

const BASE = '/api/parent-app/admin/rbac';

export interface Permission { id: number; module: string; action: string; }
export interface PermissionCatalog {
  permissions: Permission[];
  byModule: Record<string, { id: number; action: string }[]>;
}
export interface Role {
  id: number;
  uuid: string;
  code: string;
  name: string;
  description: string | null;
  is_system_role: boolean;
  is_active: boolean;
  user_count: number;
  permission_ids: number[];
  permissions: string[];
}
export interface StaffRoleRef { id: number; name: string; code: string; }
export interface StaffGroupRef { id: number; name: string; }
export interface Staff {
  id: number;
  uuid: string;
  name: string;
  email: string | null;
  phone: string | null;
  role_id: number | null;
  scope_type: 'school' | 'group' | 'section' | 'none';
  is_active: boolean;
  photo_url: string | null;
  staffRole: StaffRoleRef | null;
  classGroups: StaffGroupRef[];
}
export interface ClassGroup {
  id: number;
  uuid: string;
  name: string;
  sort_order: number;
  curriculum_class_ids: number[];
  classes: { id: number; name: string }[];
}

export const rbacApi = {
  getPermissions: () => apiRequest<PermissionCatalog>(`${BASE}/permissions`),

  // Roles
  getRoles: () => apiRequest<{ data: Role[] }>(`${BASE}/roles`),
  createRole: (body: { name: string; description?: string; permission_ids?: number[] }) =>
    apiRequest(`${BASE}/roles`, { method: 'POST', body: JSON.stringify(body) }),
  updateRole: (id: number, body: { name?: string; description?: string; is_active?: boolean }) =>
    apiRequest(`${BASE}/roles/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  setRolePermissions: (id: number, permission_ids: number[]) =>
    apiRequest(`${BASE}/roles/${id}/permissions`, { method: 'PUT', body: JSON.stringify({ permission_ids }) }),
  deleteRole: (id: number) => apiRequest(`${BASE}/roles/${id}`, { method: 'DELETE' }),

  // Staff
  getStaff: () => apiRequest<{ data: Staff[] }>(`${BASE}/staff`),
  createStaff: (body: { name: string; email?: string; phone?: string; role_id?: number; scope_type?: string }) =>
    apiRequest(`${BASE}/staff`, { method: 'POST', body: JSON.stringify(body) }),
  updateStaff: (id: number, body: Partial<{ name: string; email: string; phone: string; role_id: number; scope_type: string; is_active: boolean }>) =>
    apiRequest(`${BASE}/staff/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  setStaffClassGroups: (id: number, group_ids: number[]) =>
    apiRequest(`${BASE}/staff/${id}/class-groups`, { method: 'PUT', body: JSON.stringify({ group_ids }) }),

  // Class groups
  getClassGroups: () => apiRequest<{ data: ClassGroup[] }>(`${BASE}/class-groups`),
  createClassGroup: (body: { name: string; sort_order?: number }) =>
    apiRequest(`${BASE}/class-groups`, { method: 'POST', body: JSON.stringify(body) }),
  updateClassGroup: (id: number, body: { name?: string; sort_order?: number }) =>
    apiRequest(`${BASE}/class-groups/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  setClassGroupMembers: (id: number, curriculum_class_ids: number[]) =>
    apiRequest(`${BASE}/class-groups/${id}/classes`, { method: 'PUT', body: JSON.stringify({ curriculum_class_ids }) }),
  deleteClassGroup: (id: number) => apiRequest(`${BASE}/class-groups/${id}`, { method: 'DELETE' }),

  // Curriculum classes (for group membership picker) — reuses existing endpoint
  getCurriculumClasses: () =>
    apiRequest<{ grouped: { curriculum_id: number; curriculum_name: string; classes: { id: number; class_label: string }[] }[] }>(
      '/api/parent-app/admin/classes'),
};
